import axios from 'axios';
import * as Sentry from "@sentry/react";
import { API_BASE_URL } from '../config/apiConfig';

const getCookie = (name) => {
  if (typeof document === "undefined") return null;
  const cookies = document.cookie ? document.cookie.split(";") : [];
  for (const part of cookies) {
    const [rawKey, ...rest] = part.trim().split("=");
    if (!rawKey) continue;
    if (rawKey === name) return decodeURIComponent(rest.join("="));
  }
  return null;
};

export const normalizeApiError = (error, fallbackMessage = 'An unexpected error occurred') => {
  const message =
    error?.response?.data?.error ||
    error?.response?.data?.message ||
    error?.message ||
    fallbackMessage;

  const normalizedError = new Error(message);
  normalizedError.status = error?.response?.status ?? null;
  normalizedError.details = error?.response?.data ?? null;
  normalizedError.isNetworkError = !error?.response;

  return normalizedError;
};

const api = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: true,
  xsrfCookieName: "csrf_token",
  xsrfHeaderName: "X-CSRF-Token",
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add JWT token
api.interceptors.request.use(
  (config) => {
    config.headers = config.headers || {};
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    } else {
      delete config.headers.Authorization;
    }

    // Ensure CSRF header is present for cookie-based sessions, including cross-site and FormData.
    const method = String(config.method || "get").toUpperCase();
    const isUnsafe = !["GET", "HEAD", "OPTIONS"].includes(method);
    if (isUnsafe && !config.headers["X-CSRF-Token"] && !config.headers["x-csrf-token"]) {
      const csrf = getCookie("csrf_token");
      if (csrf) config.headers["X-CSRF-Token"] = csrf;
    }

    // For file uploads, let the browser set the Content-Type
    if (config.data instanceof FormData) {
      delete config.headers['Content-Type'];
    }

    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => response,
  (error) => {
    Sentry.addBreadcrumb({
      category: "xhr",
      message: `API Error: ${error.config?.url}`,
      level: "error",
      data: {
        method: error.config?.method,
        url: error.config?.url,
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
      },
    });

    return Promise.reject(normalizeApiError(error, 'An error occurred'));
  }
);

export default api;